import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  BookOpen, ChevronsDownUp, ChevronsUpDown, FileCode2, Search as SearchIcon,
} from "lucide-react";
import { Disclosure } from "@/components/Disclosure";
import { EmptyState, ErrorBox, PageHeader } from "@/components/common";
import { MarkdownContent } from "@/components/MarkdownContent";
import { rawFetch } from "@/lib/api/client";
import type { ContentPage } from "@/lib/siteContent";

const API_ORIGIN = (import.meta.env.VITE_API_BASE_URL || "/api/v1").replace(/\/api\/v1\/?$/, "");
const SCHEMA_URL = `${API_ORIGIN}/api/schema/`;
const SWAGGER_URL = `${API_ORIGIN}/api/docs/`;

export function Docs({ slug = "docs" }: { slug?: "docs" | "howto" | "qna" | "orb" }) {
  const [q, setQ] = useState("");
  const [allOpen, setAllOpen] = useState(false);
  const page = useQuery({
    queryKey: ["published-site-content", slug],
    queryFn: () => rawFetch<ContentPage>(`/site-content/public/pages/${slug}/`, undefined, "json", false),
  });
  const sections = page.data?.sections ?? [];
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return sections;
    return sections.filter((section) =>
      section.title.toLowerCase().includes(needle) ||
      section.tags.some((tag) => tag.toLowerCase().includes(needle)) ||
      section.content_markdown.toLowerCase().includes(needle),
    );
  }, [q, sections]);

  return (
    <>
      <PageHeader
        title={page.data?.title ?? "Read the docs"}
        subtitle={page.data?.subtitle}
        actions={slug === "docs" ? <a className="btn" href={SWAGGER_URL} target="_blank" rel="noopener"><FileCode2 size={14} /> OpenAPI / Swagger</a> : undefined}
      />
      {page.isPending && <div className="card text-sm text-muted" role="status">Loading documentation…</div>}
      {page.isError && <ErrorBox error={page.error} />}
      {page.data && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[220px_1fr]">
          <aside className="lg:sticky lg:top-2 lg:self-start">
            <div className="card">
              <label className="label flex items-center gap-1" htmlFor="docs-filter"><SearchIcon size={14} /> Filter</label>
              <input id="docs-filter" className="input mb-3" placeholder="rag, ontology, plan…" value={q} onChange={(event) => setQ(event.target.value)} />
              <button className="btn mb-3 w-full justify-center" onClick={() => setAllOpen((value) => !value)}>
                {allOpen ? <><ChevronsDownUp size={14} /> Collapse all</> : <><ChevronsUpDown size={14} /> Expand all</>}
              </button>
              <nav className="flex flex-col gap-1 text-sm" aria-label="Documentation sections">
                {filtered.map((section) => (
                  <a key={section.id} href={`#${section.slug}`} className="nav-link">
                    <BookOpen size={14} className="shrink-0" />
                    <span className="truncate">{section.title}</span>
                  </a>
                ))}
              </nav>
              <p className="mt-3 text-[11px] text-muted">{filtered.length} of {sections.length} sections. Click a title to expand.</p>
            </div>
          </aside>
          <main className="space-y-3">
            {filtered.map((section, index) => (
              <Disclosure
                key={section.id}
                id={section.slug}
                title={section.title}
                Icon={BookOpen}
                defaultOpen={index === 0}
                forceOpen={q.trim() ? true : (allOpen || undefined)}
                meta={section.tags.slice(0, 2).join(" · ")}
              >
                <MarkdownContent markdown={section.content_markdown} />
              </Disclosure>
            ))}
            {filtered.length === 0 && <EmptyState title={`No section matches “${q}”.`} />}
          </main>
        </div>
      )}
      {page.data && slug === "docs" && <p className="mt-8 text-xs text-muted">
        <a className="text-accent underline" href={SCHEMA_URL} target="_blank" rel="noopener">OpenAPI 3.1 schema</a>
      </p>}
    </>
  );
}
